import {
    ButtonGradientPrimary,
    Card,
    Icon,
    MultilineInput,
    Spinner,
    Tabs,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import StrategicVisionTranslationsTab from './StrategicVisionTranslationsTab';
import { STRATEGIC_VISION_MAX_LENGTH, useStrategicVisionDraft } from './useStrategicVisionDraft';
import { VISION_SECTION_TITLE } from './visionValuesStyles';

import {
    useCompanyPreferences,
    useUpdateStrategicVision,
} from '@/api/performance-company-preferences';
import { companyAtom } from '@/atoms/company';
import { userInstanceAtom } from '@/atoms/userDetails';
import { useSelectedAppLanguage } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { getLanguageDisplayName } from '@/utils/localization';

const VISION_FIELD_ID = 'strategic-vision-field';

// Both tabs are siblings in the same row -- one size keeps their icons visually aligned instead
// of the pair silently drifting apart (`md` for one, `sm` for the other).
const TAB_ICON_SIZE = 'md';

/**
 * The section reads as two panels joined by a frosted bridge -- what the vision is for on the
 * left, the editor on the right -- side by side once the row is wide enough, stacked below that.
 *
 * The left panel takes a *share* of the row rather than a fixed width, because the module renders
 * beside the host's side menu: the row is therefore always narrower than the canvas the design was
 * drawn on, and a fixed panel would leave the editor barely wider than its own tabs at the width
 * where the two columns first appear. This share is the drawn panel over that canvas.
 */
const HEADER_PANEL_WIDTH = 'lg:w-[45%]';

/**
 * The bridge is decoration, and its width *is* the seam: the row closes its gap exactly where the
 * bridge appears, so the panels sit flush against it and the two values cannot drift apart. Its
 * blur is heavier than the panels', which is what makes it read as a joint rather than a third
 * card.
 */
const BRIDGE = 'hidden h-18 w-11 shrink-0 self-center bg-white/45 backdrop-blur-glass-lg lg:block';

/**
 * The measure of the left panel's description, so the paragraph stays narrower than the title it
 * sits under instead of running the panel's full width. Real pixels rather than a `max-w-*` step,
 * whose scale is still relative to the host's smaller root and so renders narrower than it reads.
 */
const HEADER_DESCRIPTION_MEASURE = 'max-w-[362px]';

type Tab = 'vision' | 'translations';

export default function StrategicVisionSection() {
    const { t } = useTranslation();
    const { handleError } = useError();
    const currentLanguage = useSelectedAppLanguage();
    const company = useAtomValue(companyAtom);
    const user = useAtomValue(userInstanceAtom);
    const configuredLanguages = company?.company?.availableLanguages || user?.surveyLanguages || [];

    const { data: preferences, isLoading, error } = useCompanyPreferences();
    const updateStrategicVision = useUpdateStrategicVision();
    const [activeTab, setActiveTab] = useState<Tab>('vision');

    // Both tabs edit the same draft (the main field writes the reference language, the
    // translations tab writes any other one) so a single explicit Save, below, always
    // persists everything typed across both tabs at once — no per-tab save, no auto-save.
    const { draft, currentText, setCurrentText, replaceDraft, payload, isDirty } =
        useStrategicVisionDraft({
            savedVision: preferences?.strategicVision,
            currentLanguage,
        });

    const isSaving = updateStrategicVision.isPending;

    const handleSave = async () => {
        try {
            await updateStrategicVision.mutateAsync({ strategicVision: payload });
            showSuccessToast({ message: t('strategicVision.saveSuccess') });
        } catch (saveError) {
            handleError({ error: saveError });
        }
    };

    const tabItems = [
        {
            key: 'vision',
            dataTestId: 'strategic-vision-tab-vision',
            label: (
                <div className={'flex items-center gap-2'}>
                    <Icon
                        // A document glyph, not an image one: this tab is a text field, not a
                        // picture.
                        name={'edit-document-text'}
                        size={TAB_ICON_SIZE}
                        colorClassName={activeTab === 'vision' ? 'text-teal-500' : 'text-gray-500'}
                    />
                    {t('strategicVision.visionLabel')}
                </div>
            ),
        },
        {
            key: 'translations',
            dataTestId: 'strategic-vision-tab-translations',
            label: (
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={'lucide-languages'}
                        size={TAB_ICON_SIZE}
                        colorClassName={
                            activeTab === 'translations' ? 'text-teal-500' : 'text-gray-500'
                        }
                    />
                    {t('visionValues.translationsTab')}
                </div>
            ),
        },
    ];

    const renderTabContent = () => {
        if (activeTab === 'translations') {
            return (
                <StrategicVisionTranslationsTab
                    vision={draft}
                    configuredLanguages={configuredLanguages}
                    currentLanguage={currentLanguage}
                    onChange={replaceDraft}
                />
            );
        }

        return (
            <div className={'flex flex-col gap-3'}>
                <label
                    htmlFor={VISION_FIELD_ID}
                    className={'text-sm text-gray-500'}
                >
                    {t('strategicVision.referenceLanguage', {
                        language: getLanguageDisplayName(currentLanguage),
                    })}
                </label>

                <MultilineInput
                    id={VISION_FIELD_ID}
                    value={currentText}
                    onChange={setCurrentText}
                    rows={4}
                    maxLength={STRATEGIC_VISION_MAX_LENGTH}
                    showCharacterCount
                    variant={'white'}
                    placeholder={t('strategicVision.placeholder')}
                    dataTestId={'strategic-vision-input'}
                />
            </div>
        );
    };

    const renderEditor = () => {
        if (isLoading) {
            return (
                <div className={'flex justify-center py-10'}>
                    <Spinner dataTestId={'strategic-vision-loader'} />
                </div>
            );
        }

        if (error) {
            return (
                <p
                    role={'alert'}
                    className={'py-6 text-red-500'}
                >
                    {t('strategicVision.errorLoadingStrategicVision')}
                </p>
            );
        }

        return (
            <>
                <div className={'mb-6 border-b border-gray-200'}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as Tab)}
                        items={tabItems}
                    />
                </div>

                {renderTabContent()}

                <div className={'mt-6 flex flex-wrap justify-start gap-3'}>
                    <ButtonGradientPrimary
                        onClick={handleSave}
                        disabled={!isDirty || isSaving}
                        size={'md'}
                        dataTestId={'strategic-vision-save'}
                    >
                        {t('strategicVision.save')}
                    </ButtonGradientPrimary>
                </div>
            </>
        );
    };

    return (
        <div
            className={'flex flex-col gap-4 lg:flex-row lg:gap-0'}
            data-testid={'strategic-vision-section'}
        >
            <Card
                padding={'lg'}
                borderRadius={'2xl'}
                className={`items-center justify-center gap-6 text-center ${HEADER_PANEL_WIDTH}`}
                dataTestId={'strategic-vision-header'}
            >
                <div
                    className={
                        'flex items-center justify-center !rounded-lg bg-gray-100 p-4 shadow-sm'
                    }
                >
                    <Icon
                        name={'security-eye'}
                        size={35}
                        colorClassName={'text-teal-400'}
                        strokeWidth={2}
                    />
                </div>

                <div className={'flex flex-col items-center gap-1.5'}>
                    <h2 className={VISION_SECTION_TITLE}>{t('strategicVision.title')}</h2>

                    <p
                        className={`text-base font-medium text-gray-500 ${HEADER_DESCRIPTION_MEASURE}`}
                    >
                        {t('strategicVision.description')}
                    </p>
                </div>
            </Card>

            <div className={BRIDGE} />

            <Card
                padding={'lg'}
                borderRadius={'2xl'}
                className={'lg:min-w-0 lg:flex-1'}
                dataTestId={'strategic-vision-editor'}
            >
                {renderEditor()}
            </Card>
        </div>
    );
}
